import React, { useEffect, useState, useContext, useRef, useMemo } from "react"
import { SearchBar } from "../components/SearchBar";
import {Grid, Container, Typography } from '@mui/material'
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import { useInView } from 'react-intersection-observer';
import { LayoutTabs } from "../components/LayoutTabs";
import { useMattoState } from "../../MattoState";
import { CollectionInterface, ProjectSchemeInterface } from "ts-interfaces";
import { DisplayCollectionSchemes } from "./DisplayCollectionSchemes";
import { CardTypes } from "ts-interfaces";
import { COLLECTION_TYPES, useCollections } from "../../global-state/useCollections";
import { AuthContext } from "../../auth/Auth";
import { ResponsiveGridLayout } from "../components/ResponsiveGridLayout";
import { useProjectSchemas } from "../../global-state/useProjectSchemas";

interface CollectionSchemeProps {
    designFirms: any; // replace with actual type
    teamMembers: any; // replace with actual type
    permissions: any; // replace with actual permissions type
    associatedProjectsList: ProjectSchemeInterface[];
    showCheckboxes?: boolean;
    selectedCollectionIds?: Set<string>;
    selectedProjectId?: string;
    allProjects?: ProjectSchemeInterface[];
    onCollectionSelectionChange?: (id: string, checked: boolean) => void;
}

export const CollectionScheme = ({ 
    designFirms, 
    teamMembers, 
    permissions, 
    associatedProjectsList, 
    showCheckboxes = false, 
    selectedCollectionIds = new Set(), 
    selectedProjectId = "", 
    allProjects = [],
    onCollectionSelectionChange = (id: string, checked: boolean) => {} 
}: CollectionSchemeProps) => {
    const {currentUser} = useContext(AuthContext);
    const { collections, currentUserId, isLoadingUserCollections, hasMoreUserCollections, getNext } = useCollections( (state) => ({
        collections: state.collections, 
        currentUserId: state.currentUserId,
        isLoadingUserCollections: state.isLoadingUserCollections,
        hasMoreUserCollections: state.hasMoreUserCollections, 
        getNext: state.getNext,
    }));
    
    
    const { ownedCollections, sharedCollections } = useMemo(() => {
        const owned = collections.filter(c => 
            (c.collectionType === COLLECTION_TYPES.INDIVIDUAL && c.uid === currentUserId) || 
            (c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid === currentUserId) || 
            (c.collectionType === COLLECTION_TYPES.SHARED_PROJECT && c.uid === currentUserId)
        );
        const shared = collections.filter(c => 
            (c.collectionType === COLLECTION_TYPES.SHARED && c.uid !== currentUserId) || 
            (c.collectionType === COLLECTION_TYPES.SHARED_PROJECT && c.uid !== currentUserId) || 
            (c.collectionType === COLLECTION_TYPES.OWNED_PROJECT && c.uid !== currentUserId)
        );
        return { ownedCollections: owned, sharedCollections: shared };
    }, [collections, currentUserId]);
    const [initialLoadAttempted, setInitialLoadAttempted] = useState(false);
    const [tab, setTab] = useState(0)
    const [filterString, setFilterString] = useState('');

    console.log('ownedCollections', ownedCollections);
    console.log('sharedCollections', sharedCollections);

    useEffect(() => { 
        // Only fetch when BOTH currentUser.uid AND permissions are available
        if (currentUser?.uid) {
            console.log('fetchCollections with valid permissions:', currentUser.uid);
            useCollections.getState().fetchCollections(currentUser.uid, permissions);
            setInitialLoadAttempted(true);
        }
    },[currentUser?.uid]);
    
    let currentCollections = tab === 0 ? ownedCollections : sharedCollections;    

    if (filterString) {
        currentCollections = currentCollections.filter((collection: CollectionInterface) => {
            const collectionName = collection.name || '';
            return collectionName.toLowerCase().includes(filterString.toLowerCase());
        });
    }

    const rootProjectContainer = useRef<HTMLDivElement>(null)
    const { ref, inView } = useInView({root:null, threshold: 0});
    
    const loadItems = async () => {
        if (isLoadingUserCollections === false && hasMoreUserCollections) {  // ✅ Updated to use new property name
            await getNext();
        } 
    }

    useEffect(() => {  if (inView==true && initialLoadAttempted){ loadItems() } },[inView, initialLoadAttempted])

    const createNewCollection = () => {
        useMattoState.getState().setModal({name:'collection', id:null, associatedProjectsList: associatedProjectsList});    
    }

    const handleCollectionUpdate = (collection: CollectionInterface, addCollection = false, isDeleted = false) =>  { 
        if (addCollection) { useCollections.getState().saveCollection(collection); } 
        else if (isDeleted) {
            useCollections.getState().removeCollection(collection.id);
        } else {
            useCollections.getState().updateCollection(collection);
        }
        if(isDeleted) {
            useProjectSchemas.getState().removeCollectionFromProjects(collection.id);
        } else {
            useProjectSchemas.getState().updateCollectionInProjects(collection);
        }

    }

    const handleTabSwitch = (tabType: any) => void setTab(tabType);

    return (
        <Grid ref={rootProjectContainer} sx={{ p: showCheckboxes ? 3 : 0 }}>
            <Container maxWidth={false}>
                <SearchBar setFilterString={setFilterString} placeholder="Search by Collection Name" />
                <LayoutTabs tabType="My Collections" teamMembers={teamMembers} onTabSwitch={handleTabSwitch} />
                <Grid sx={{ marginTop: '20px' }} />
                <ResponsiveGridLayout 
                    showNewCard={!showCheckboxes && tab === 0}
                    newCardProps={
                        tab === 0 ?
                        {
                            title: "New Collection",
                            description: "Start a new collection from scratch",
                            type: CardTypes.Collections,
                            onClick: () => createNewCollection()
                        } : undefined
                    }
                >
                    <DisplayCollectionSchemes 
                        data={currentCollections} 
                        onCollectionUpdate={handleCollectionUpdate} 
                        designFirms={designFirms} 
                        teamMembers={teamMembers} 
                        associatedProjectsList={associatedProjectsList} 
                        showCheckboxes={showCheckboxes}
                        selectedCollectionIds={selectedCollectionIds}
                        selectedProjectId={selectedProjectId}
                        allProjects={allProjects}
                        onCollectionSelectionChange={onCollectionSelectionChange}
                    />
                </ResponsiveGridLayout>   
                {currentCollections.length >=20 && hasMoreUserCollections && (  // ✅ Updated to use new property name
                    <Grid item 
                      sx={{position:'absolute', bottom: '10px', left: 0, right: 0, margin: '0 auto', width: '36px', cursor: 'pointer', '&:hover': {border: '1px solid #ccc' }}}
                      onClick={loadItems}>
                        <KeyboardDoubleArrowDownIcon sx={{fontSize: 36, color: '#808080'}} />
                    </Grid>
                )}
                {tab === 1 && currentCollections.length === 0 && (
                    <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '50vh',
                            textAlign: 'center',
                            color: '#555',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                            No Shared Collections
                        </Typography>
                        <Typography variant="body1">
                            You currently have no collections shared with you. Please check back later or contact your team members.
                        </Typography>
                    </Grid>
                )} 
            </Container>
            <Grid key={'placeholder'} ref={ref} item xs={6}>
                <img draggable={false} src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg==" height={1} alt="Red dot" />
            </Grid>
        </Grid>
    )
}